import type { ProductCategoryConfig } from '../../api/productCategory/productCategoryApiSlice';
import type { TenantServiceConfig } from '../../api/account/accountApiTypes';
import { hasMinStay, resolveBookingWindow, toMinutes, toUnit } from './bookingWindowRules';
import { unmetPrerequisiteForDirectBooking } from './serviceConfigurationRules';
import type { DepositedPrerequisites, Prerequisite } from './serviceConfigurationRules';
import type { TenantChargingMode } from '../../api/account/accountApiTypes';
import { categorySettingsOf } from '../../utils/useCases';
import {
    cancellationDraftFrom,
    cancellationFromDraft,
    cancellationOnCategory,
    cancellationOnUseCase,
} from './cancellationRules';
import type { CancellationDraft } from './cancellationRules';
import type { TenantCancellationRule } from '../../api/account/accountApiTypes';

/**
 * Turning a category's stored configuration into form state and back
 * (ADR-0026 §B).
 *
 * Kept out of the component because this is where the layer's one real trap
 * lives: **absent is not the same as off.** An absent `directBooking` means
 * "whatever the use case says"; `false` means "not on this category, whatever
 * the use case says". A round trip that collapsed the two would silently
 * rewrite a tenant's configuration the first time anyone opened the form and
 * pressed save without touching a thing — which is the kind of defect nobody
 * reports, because it looks like nothing happened.
 *
 * `categoryConfigRules.test.ts` therefore drives the round trip, not just the
 * two directions separately.
 */

/** The sentinel a select uses for "inherit from the use case". */
export const INHERIT = 'inherit';

/*
 * Where each category setting is configurable is a property of the use case
 * (`utils/useCases.ts`, ADR-0026 §AU). The server copies are the enforcing ones
 * (`new-backend/admin/src/lib/serviceConfiguration.ts` and
 * `categoryConfigInput.ts`), where these sets are still named lists:
 *   - event vocabulary and event input (`EVENT_TYPE_USE_CASES`,
 *     `EVENT_TYPE_INPUT_USE_CASES`): `eventTypes`;
 *   - the customer-facing note (`NOTE_USE_CASES`): `categoryNote` — the
 *     venue booking step is the one screen showing it;
 *   - the rate-unit knobs (`RATE_UNIT_USE_CASES`): `rateUnits` — the
 *     resolver's weekend modes and sub-day units are the venue rate rule (#1713);
 *   - entry sequence, head-count word (#3035), sign-in first (#3069)
 *     (`USAGE_PATTERN_USE_CASES`): `usagePatterns`.
 * Each is a category-setting property, carried by the space AND the
 * station-unit block (`categorySettingsOf`, #3429 step 2) — never the
 * template. On a station unit every one is false until a booking screen of
 * that use case reads it.
 */

/** The category's event vocabulary and event input are configurable. */
export const hasEventTypes = (useCase: string): boolean => categorySettingsOf(useCase)?.eventTypes === true;
/** The category's customer-facing note is configurable. */
export const hasCategoryNote = (useCase: string): boolean => categorySettingsOf(useCase)?.categoryNote === true;
/** The category's rate-unit knobs are configurable. */
export const hasRateUnits = (useCase: string): boolean => categorySettingsOf(useCase)?.rateUnits === true;
/** The usage-pattern choices (entry sequence, head-count word, sign-in first) are configurable. */
export const hasUsagePatterns = (useCase: string): boolean => categorySettingsOf(useCase)?.usagePatterns === true;

/** #3035: the two entry orders and the head-count words, defaults first.
 *  The default is stored as ABSENCE — one shape for "today's behaviour". */
export const ENTRY_SEQUENCES = ['dateFirst', 'objectFirst'] as const;
export const HEAD_COUNT_LABELS = ['guests', 'participants', 'members'] as const;
/** #3069: when the flow asks who the customer is. Mirrors `IDENTIFICATIONS` in
 *  `new-backend/admin/src/lib/categoryConfigInput.ts` (enforcing) and the served
 *  list in `new-backend/app/src/lib/tenantConfig.ts`. */
export const IDENTIFICATIONS = ['optional', 'first'] as const;

/**
 * ADR-0026 §X: how a space is USED, offered as a preset — a curated bundle of
 * the space template's choices, so a tenant gets a coherent configuration
 * without knowing the individual switches. A preset writes ordinary
 * overrides: choosing one fills the fields below it, and what is saved is
 * those fields, nothing else. `custom` is no preset — the fields were set
 * individually.
 *
 *   - `oneOff`: an occasion booked once (a wedding, a seminar) — today's flow.
 *   - `serial`: a space used across a season by clubs, schools, members (the
 *     sports hall): the room first and its week view, "participants", sign-in
 *     before the first step, the event named in free text.
 */
export const USAGE_PATTERNS = ['oneOff', 'serial'] as const;
export type UsagePattern = (typeof USAGE_PATTERNS)[number] | 'custom';

/**
 * The fields each pattern sets. ONE DEFINITION:
 * `shared/use-cases/usage-patterns.json`, which this copy must equal
 * (`categoryConfigRules.test.ts`). The server applies the same definition when
 * a standard category is born with a preset (the hall's "Sporthallen", PO
 * 2026-10-18; `new-backend/admin/src/lib/catalogue/usagePatterns.ts`). Each
 * `oneOff` value is the field's default, stored as absence.
 */
export const USAGE_PATTERN_FIELDS: Readonly<Record<(typeof USAGE_PATTERNS)[number], Readonly<{
    entrySequence: (typeof ENTRY_SEQUENCES)[number];
    headCountLabel: (typeof HEAD_COUNT_LABELS)[number];
    identification: (typeof IDENTIFICATIONS)[number];
    eventTypeInput: CategoryConfigDraft['eventTypeInput'];
}>>> = {
    oneOff: { entrySequence: 'dateFirst', headCountLabel: 'guests', identification: 'optional', eventTypeInput: 'select' },
    serial: { entrySequence: 'objectFirst', headCountLabel: 'participants', identification: 'first', eventTypeInput: 'freeText' },
};

/** The fields a usage pattern sets, for this use case: the event input only
 *  where the use case asks for the event. */
export const usagePatternChoices = (
    pattern: (typeof USAGE_PATTERNS)[number],
    useCase: string,
): Partial<CategoryConfigDraft> => {
    const { eventTypeInput, ...choices } = USAGE_PATTERN_FIELDS[pattern];
    return hasEventTypes(useCase) ? { ...choices, eventTypeInput } : { ...choices };
};

/** The pattern a draft matches, or `custom`. */
export const usagePatternOf = (draft: CategoryConfigDraft, useCase: string): UsagePattern =>
    USAGE_PATTERNS.find((pattern) =>
        Object.entries(usagePatternChoices(pattern, useCase)).every(
            ([field, value]) => draft[field as keyof CategoryConfigDraft] === value,
        ),
    ) ?? 'custom';

/** The weekend-mode select's "no mode stored" state. NOT the same answer as
 *  the other fields' `inherit`: there is no use-case value behind it — absent
 *  means the LEGACY weekend formula keeps running, bit-for-bit. */
export const RATE_MODE_LEGACY = 'legacy';

/** What the server accepts for the two time knobs ('HH:mm'). */
export const RATE_TIME_SHAPE = /^([01]?\d|2[0-3]):[0-5]\d$/;

export interface CategoryConfigDraft {
    checkIn: string;
    charging: string;
    directBooking: string;
    eventTypes: string;
    eventTypesText: string;
    /** The list as stored, so an untouched list is sent back exactly — a
     *  label containing a comma must not be split by a save nobody made. */
    eventTypesStored?: string[];
    payment: string;
    paymentFlow: string;
    paymentChannels: string[];
    bookingWindow: string;
    leadMinutes: string;
    horizonMonths: string;
    followUpMinutes: string;
    /** Nights; apartments only. Blank = one night. */
    minNights: string;
    noteDe: string;
    noteEn: string;
    /** `select` (the dropdown, also the stored absence) or `freeText`. */
    eventTypeInput: 'select' | 'freeText';
    rateWeekendMode: string;
    rateWeekendStart: string;
    rateHalfDayCutoff: string;
    entrySequence: string;
    headCountLabel: string;
    identification: string;
    /** M10: the cancellation section — see `cancellationRules.ts`. */
    cancellation: CancellationDraft;
}

/**
 * Lead time and the follow-up buffer are entered in the USE CASE'S OWN UNIT —
 * days for a whole-day use case, minutes for the rest — and converted here.
 *
 * Not cosmetic: `invalidBookingWindow` on the server REFUSES a lead time that
 * is not a whole number of days for `apartment` and `venue`. A field labelled
 * "minutes" would let an operator type 120 for a venue and collect a 400 they
 * have no way to read. Found by running the validator rather than reading it.
 *
 * `toUnit`/`toMinutes` are the conversion the per-use-case booking-window
 * fields already use (`bookingWindowRules.ts`) — imported rather than
 * restated, so the two forms cannot disagree about what a "3" means. The unit
 * LABEL comes from `bufferUnitFor` there for the same reason: the units are
 * days and hours, and a locally invented "minutes" would have rendered a raw
 * translation key (#2223).
 */
const numberText = (
    minutes: number | undefined,
    useCase: string,
): string => (typeof minutes === 'number' ? String(toUnit(minutes, useCase)) : '');

/** Form state for a category, defaulting every field to "inherits". */
export const draftFromConfig = (
    config: ProductCategoryConfig | undefined,
    useCase: string,
): CategoryConfigDraft => ({
    // Two states only — inherit, or off. A category cannot switch a flow ON;
    // that needs the use-case editor's whole authoring surface, and the server
    // refuses it. See `categoryConfigInput.ts`.
    checkIn: config?.checkInCheckOut ? 'off' : INHERIT,
    charging: config?.charging ?? INHERIT,
    // `?? INHERIT` would be wrong for a stored `false`, which is a real
    // answer and not an absence — hence the explicit undefined test.
    directBooking:
        config?.directBooking === undefined ? INHERIT : String(config.directBooking),
    eventTypes: config?.eventTypes ? 'override' : INHERIT,
    eventTypesText: (config?.eventTypes ?? []).join(', '),
    ...(config?.eventTypes ? { eventTypesStored: config.eventTypes } : {}),
    payment: config?.payment ? 'override' : INHERIT,
    paymentFlow: config?.payment?.flow ?? '',
    paymentChannels: config?.payment?.channels ?? [],
    bookingWindow: config?.bookingWindow ? 'override' : INHERIT,
    leadMinutes: numberText(config?.bookingWindow?.leadMinutes, useCase),
    // Months are months in every use case, so this one is not converted.
    horizonMonths:
        typeof config?.bookingWindow?.horizonMonths === 'number'
            ? String(config.bookingWindow.horizonMonths)
            : '',
    followUpMinutes: numberText(config?.bookingWindow?.followUpMinutes, useCase),
    // Nights are nights in every unit, so not converted either.
    minNights:
        typeof config?.bookingWindow?.minNights === 'number'
            ? String(config.bookingWindow.minNights)
            : '',
    noteDe: config?.note?.de ?? '',
    noteEn: config?.note?.en ?? '',
    eventTypeInput: config?.eventTypeInput === 'freeText' ? 'freeText' : 'select',
    rateWeekendMode: config?.rateUnits?.weekendMode ?? RATE_MODE_LEGACY,
    rateWeekendStart: config?.rateUnits?.weekendStart ?? '',
    rateHalfDayCutoff: config?.rateUnits?.halfDayCutoff ?? '',
    entrySequence: config?.entrySequence ?? ENTRY_SEQUENCES[0],
    headCountLabel: config?.headCountLabel ?? HEAD_COUNT_LABELS[0],
    identification: config?.identification ?? IDENTIFICATIONS[0],
    cancellation: cancellationDraftFrom(config?.cancellation, false),
});

const wholeNumber = (raw: string): number | undefined => {
    const text = raw.trim();
    if (!/^\d+$/.test(text)) return undefined;
    return Number(text);
};

/**
 * #3035, #3069: the space template's curated choices. Only a NON-default
 * choice is stored, and only where the server accepts it — a stale draft
 * cannot post a field it would refuse.
 */
const spaceChoicesFromDraft = (draft: CategoryConfigDraft, useCase: string): ProductCategoryConfig => {
    if (!hasUsagePatterns(useCase)) return {};
    const choices: ProductCategoryConfig = {};
    if (draft.entrySequence === 'objectFirst') choices.entrySequence = 'objectFirst';
    if (draft.headCountLabel === 'participants' || draft.headCountLabel === 'members') {
        choices.headCountLabel = draft.headCountLabel;
    }
    if (draft.identification === 'first') choices.identification = 'first';
    return choices;
};

/**
 * The block to PUT.
 *
 * A field left on "inherits" is OMITTED rather than sent as null, so the
 * request says only what the tenant actually decided. The endpoint treats an
 * empty body as "remove this category's overrides", which is exactly what a
 * form with everything on "inherits" should mean.
 *
 * @param useCase the category's use case — event types are dropped for the
 *   ones that cannot have them, so a stale draft cannot post a field the
 *   server would refuse.
 */
export const configFromDraft = (
    draft: CategoryConfigDraft,
    useCase: string,
): ProductCategoryConfig => {
    const config: ProductCategoryConfig = {};

    if (draft.checkIn === 'off') {
        config.checkInCheckOut = { enabled: false };
    }
    if (draft.charging !== INHERIT) {
        config.charging = draft.charging as ProductCategoryConfig['charging'];
    }
    if (draft.directBooking !== INHERIT) {
        config.directBooking = draft.directBooking === 'true';
    }

    const types = eventTypesFromDraft(draft, useCase);
    if (types) config.eventTypes = types;

    const payment = paymentFromDraft(draft);
    if (payment) config.payment = payment;

    const window = bookingWindowFromDraft(draft, useCase);
    if (window) config.bookingWindow = window;

    const note = noteFromDraft(draft, useCase);
    if (note) config.note = note;

    // `select` is the default and is stored as ABSENCE — one spelling for
    // "the dropdown". Dropped for use cases without the field, so a stale
    // draft cannot post what the server refuses.
    if (draft.eventTypeInput === 'freeText' && hasEventTypes(useCase)) {
        config.eventTypeInput = 'freeText';
    }

    const rateUnits = rateUnitsFromDraft(draft, useCase);
    if (rateUnits) config.rateUnits = rateUnits;

    Object.assign(config, spaceChoicesFromDraft(draft, useCase));

    // A further category's own rule, "none", or a clearing null — and only
    // where the server accepts one (room categories).
    if (cancellationOnCategory(useCase)) {
        const cancellation = cancellationFromDraft(draft.cancellation, false);
        if (cancellation.send) config.cancellation = cancellation.value;
    }

    return config;
};

/**
 * A time knob that is filled in but not a time of day. The form refuses to
 * save in this state — the server would 400 with a message the operator has
 * no field to connect to.
 */
export const rateTimesInvalid = (draft: CategoryConfigDraft): boolean =>
    [draft.rateWeekendStart, draft.rateHalfDayCutoff].some(
        (raw) => raw.trim() !== '' && !RATE_TIME_SHAPE.test(raw.trim()),
    );

/**
 * The category's rate-unit knobs (#1713), or nothing.
 *
 * Mirrors the form's visibility exactly: `legacy` stores NOTHING (absence is
 * the one spelling of "the previous weekend formula"), `package` stores the
 * mode with its Friday split and the cutoff, `per_day` the mode and the
 * cutoff. A time the form hides is dropped, never silently stored — an
 * invisible value that changes prices is the trap this layer exists to
 * avoid.
 */
const rateUnitsFromDraft = (
    draft: CategoryConfigDraft,
    useCase: string,
): ProductCategoryConfig['rateUnits'] | undefined => {
    // Dropped for a use case the server refuses them on, as event types are.
    if (!hasRateUnits(useCase)) return undefined;
    const mode = draft.rateWeekendMode;
    if (mode !== 'package' && mode !== 'per_day') return undefined;
    const rateUnits: NonNullable<ProductCategoryConfig['rateUnits']> = {
        weekendMode: mode,
    };
    const weekendStart = draft.rateWeekendStart.trim();
    if (mode === 'package' && RATE_TIME_SHAPE.test(weekendStart)) {
        rateUnits.weekendStart = weekendStart;
    }
    const halfDayCutoff = draft.rateHalfDayCutoff.trim();
    if (RATE_TIME_SHAPE.test(halfDayCutoff)) rateUnits.halfDayCutoff = halfDayCutoff;
    return rateUnits;
};

/**
 * An own arrangement chosen but not finished — a flow without a channel, or no
 * flow at all. The form refuses to save in this state rather than sending
 * nothing: the endpoint stores the block it is sent, so an omitted
 * arrangement would DELETE the one already stored, and the save would look
 * like it worked.
 */
export const paymentIncomplete = (draft: CategoryConfigDraft): boolean =>
    draft.payment === 'override'
    && (draft.paymentFlow === '' || draft.paymentChannels.length === 0);

/**
 * The category's own settlement arrangement, or nothing.
 *
 * Offered only as a WHOLE: a flow with no channel is one the server refuses,
 * so an unfinished answer is omitted rather than sent. Before this existed the
 * form read the arrangement into the draft and never wrote it back — every
 * save, of any field, silently removed a category's payment override, because
 * the endpoint stores the block it is sent.
 */
const paymentFromDraft = (
    draft: CategoryConfigDraft,
): ProductCategoryConfig['payment'] | undefined => {
    if (draft.payment !== 'override') return undefined;
    if (draft.paymentFlow === '' || draft.paymentChannels.length === 0) return undefined;
    return { flow: draft.paymentFlow, channels: draft.paymentChannels };
};

/** The note, trimmed, in the languages actually filled in — or nothing. */
const noteFromDraft = (
    draft: CategoryConfigDraft,
    useCase: string,
): ProductCategoryConfig['note'] | undefined => {
    // Dropped for a use case that cannot show it, as event types are.
    if (!hasCategoryNote(useCase)) return undefined;
    const note: NonNullable<ProductCategoryConfig['note']> = {};
    if (draft.noteDe.trim()) note.de = draft.noteDe.trim();
    if (draft.noteEn.trim()) note.en = draft.noteEn.trim();
    return Object.keys(note).length > 0 ? note : undefined;
};

/** The tenant's own event vocabulary, or nothing. */
const eventTypesFromDraft = (
    draft: CategoryConfigDraft,
    useCase: string,
): string[] | undefined => {
    if (draft.eventTypes !== 'override') return undefined;
    // Dropped for a use case that cannot have them, so a stale draft cannot
    // post a field the server would refuse.
    if (!hasEventTypes(useCase)) return undefined;
    if (draft.eventTypesStored && draft.eventTypesText === draft.eventTypesStored.join(', ')) {
        return draft.eventTypesStored;
    }

    const types = draft.eventTypesText
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);

    // An empty list is not an answer — it would render an empty dropdown,
    // which reads as a broken screen rather than as a choice. Nothing usable
    // therefore means "inherit" and the field is omitted.
    return types.length > 0 ? types : undefined;
};

/** The category's own booking window, or nothing. */
const bookingWindowFromDraft = (
    draft: CategoryConfigDraft,
    useCase: string,
): ProductCategoryConfig['bookingWindow'] | undefined => {
    if (draft.bookingWindow !== 'override') return undefined;

    const window: NonNullable<ProductCategoryConfig['bookingWindow']> = {};
    // Converted back out of the use case's unit — see `numberText`.
    const lead = wholeNumber(draft.leadMinutes);
    const horizon = wholeNumber(draft.horizonMonths);
    const followUp = wholeNumber(draft.followUpMinutes);
    if (lead !== undefined) window.leadMinutes = toMinutes(lead, useCase);
    if (horizon !== undefined) window.horizonMonths = horizon;
    if (followUp !== undefined) window.followUpMinutes = toMinutes(followUp, useCase);
    // The server replaces the use case's whole window with a category's, so the
    // minimum stay is always sent for an apartment category. A blank field is
    // sent as 1 (no minimum): the server keeps the STORED value when the field
    // is absent (`minNightsToKeep`), so leaving it out would make clearing it
    // a save that changes nothing.
    if (hasMinStay(useCase)) window.minNights = wholeNumber(draft.minNights) ?? 1;

    return Object.keys(window).length > 0 ? window : undefined;
};

/**
 * What a category inherits — the values of its use case's STANDARD product
 * category (ADR-0026 §S, and the 2026-10-01 PO decision that the standard
 * category IS the use case's stored entry).
 *
 * Shown, never stored. The form renders each field left on "inherits" as this
 * value with a "Standardwert" caption; nothing here enters the draft unless the
 * operator switches a field to an own value. Writing it in on render would
 * turn every visit to the form into a silent override and empty the fallback
 * §J depends on — `categoryConfigRules.test.ts` pins that an untouched form
 * still sends `{}` with these values present.
 */
export interface InheritedValues {
    /** False while the standard category was never saved: the values are the
     *  proposed standard, not yet what the tenant's bookings run on. */
    saved: boolean;
    checkIn: boolean;
    charging: 'free' | 'mixed' | 'chargeable';
    directBooking: boolean;
    /** Absent when the standard is free, or has no channel yet. */
    payment?: { flow: string, channels: string[] };
    /** Absent = the standard event-type list. */
    eventTypes?: string[];
    bookingWindow: {
        leadMinutes: number;
        horizonMonths: number;
        followUpMinutes: number;
        minNights: number;
    };
    /** M10: the standard category's cancellation rule; absent = no rule (the AGB governs). */
    cancellation?: TenantCancellationRule;
}

/**
 * The draft fields to set when the operator switches ONE field from "inherits"
 * to an own value: the inherited value, as the starting point to edit from.
 *
 * This is a reaction to a click, not to rendering — the operator has decided
 * to override, so starting from what was in force rather than from blanks is
 * what makes the override an edit instead of a re-entry. It is also what
 * removes the old "all three window numbers are required" trap: they arrive
 * filled.
 */
export const overrideStartingPoint = (
    field: 'payment' | 'bookingWindow' | 'eventTypes',
    inherited: InheritedValues,
    useCase: string,
    standardList: string[],
): Partial<CategoryConfigDraft> => {
    switch (field) {
        case 'payment':
            return {
                payment: 'override',
                paymentFlow: inherited.payment?.flow ?? '',
                paymentChannels: inherited.payment ? [...inherited.payment.channels] : [],
            };
        case 'bookingWindow':
            return {
                bookingWindow: 'override',
                leadMinutes: String(toUnit(inherited.bookingWindow.leadMinutes, useCase)),
                horizonMonths: String(inherited.bookingWindow.horizonMonths),
                followUpMinutes: String(toUnit(inherited.bookingWindow.followUpMinutes, useCase)),
                minNights: String(inherited.bookingWindow.minNights),
            };
        case 'eventTypes':
            return {
                eventTypes: 'override',
                eventTypesText: (inherited.eventTypes ?? standardList).join(', '),
            };
    }
};

/**
 * The STANDARD product category's values (ADR-0026 §AE/§AF), handed to the one
 * form both kinds of category share.
 *
 * Its booking fields are the use case's entry (`payments.useCases[<useCase>]`),
 * so they come from the account rather than from the category row; its
 * category-only fields come from the row's own `config` like any category's.
 */
export interface StandardValues {
    /** What is stored, or — never saved — the proposed standard. */
    entry: TenantServiceConfig;
    /** Our template, for "Auf Standard zurücksetzen" (§AJ). */
    template: TenantServiceConfig;
    /** False while the standard was never saved: `entry` is the proposal. */
    saved: boolean;
    checkInEnabled: boolean;
    /** Whether the use case has an authored check-in flow to switch on. */
    checkInAuthored: boolean;
}

/**
 * Form state for the standard category: every booking field as a CONCRETE
 * value — there is nothing above it to inherit from but the template, which is
 * what its values were seeded from. Only the event-type list keeps a two-state
 * select, because "the standard list" is a real, stored answer (absence).
 */
export const draftFromStandard = (
    entry: TenantServiceConfig,
    categoryConfig: ProductCategoryConfig | undefined,
    checkInEnabled: boolean,
    useCase: string,
): CategoryConfigDraft => {
    const window = resolveBookingWindow(entry.bookingWindow, useCase);
    return {
        ...draftFromConfig(categoryConfig, useCase),
        checkIn: checkInEnabled ? 'on' : 'off',
        charging: entry.charging,
        directBooking: String(entry.directBooking),
        eventTypes: entry.eventTypes ? 'override' : INHERIT,
        eventTypesText: (entry.eventTypes ?? []).join(', '),
        eventTypesStored: entry.eventTypes,
        payment: 'override',
        paymentFlow: entry.payment?.flow ?? '',
        paymentChannels: entry.payment?.channels ? [...entry.payment.channels] : [],
        bookingWindow: 'override',
        leadMinutes: String(toUnit(window.leadMinutes, useCase)),
        horizonMonths: String(window.horizonMonths),
        followUpMinutes: String(toUnit(window.followUpMinutes, useCase)),
        minNights: String(window.minNights),
        cancellation: cancellationDraftFrom(entry.cancellation, true),
    };
};

/**
 * The standard category's PUT body: a COMPLETE booking answer — the server
 * stores it as the use-case entry, where `directBooking` and `charging` are
 * required — plus the category-only fields, plus check-in on or off.
 * Free removes the payment arrangement, as it always has on the use case.
 */
export const standardConfigFromDraft = (
    draft: CategoryConfigDraft,
    useCase: string,
): ProductCategoryConfig => {
    const config = configFromDraft(draft, useCase);
    config.checkInCheckOut = { enabled: draft.checkIn === 'on' };
    if (config.charging === 'free') delete config.payment;
    // The use case's own rule (M10): sent only once the operator changed the
    // section — "no rule" is then null. Untouched, or where it is not
    // configurable, the field is left out and the server keeps what it has.
    delete config.cancellation;
    const cancellation = cancellationFromDraft(draft.cancellation, true);
    if (cancellationOnUseCase(useCase) && cancellation.send) {
        config.cancellation = cancellation.value;
    }
    return config;
};

/**
 * "Auf Standard zurücksetzen" (ADR-0026 §AJ), as form state — the operator
 * still saves, so Cancel undoes it.
 *
 * Standard category: our template, check-in off, the category-only fields at
 * their defaults. Further category: every field back on "Standardwert", i.e.
 * the standard category's value, and the category-only fields at their
 * defaults (they are never inherited, §AG).
 */
export const resetDraft = (
    useCase: string,
    standard: StandardValues | undefined,
): CategoryConfigDraft =>
    (standard
        ? draftFromStandard(standard.template, undefined, false, useCase)
        : draftFromConfig(undefined, useCase));

/**
 * An unfinished arrangement. On a further category see `paymentIncomplete`;
 * the standard category has no "inherits" to fall back to, so there it is
 * incomplete whenever the offer is not free.
 */
export const draftPaymentInvalid = (draft: CategoryConfigDraft, isStandard: boolean): boolean => {
    if (!isStandard) return paymentIncomplete(draft);
    if (draft.charging === 'free') return false;
    return draft.paymentFlow === '' || draft.paymentChannels.length === 0;
};

/**
 * Why direct booking cannot run as the form now stands, or null. Checked
 * against what will APPLY: a further category left on "inherits" runs on the
 * standard category's charging and booking mode.
 */
export const directBookingPrerequisite = (
    draft: CategoryConfigDraft,
    inherited: InheritedValues,
    deposited: DepositedPrerequisites,
): Prerequisite | null => {
    const on = draft.directBooking === INHERIT
        ? inherited.directBooking
        : draft.directBooking === 'true';
    if (!on) return null;
    const charging = (draft.charging === INHERIT ? inherited.charging : draft.charging) as TenantChargingMode;
    return unmetPrerequisiteForDirectBooking(charging, deposited);
};

/**
 * The draft change for a new charging mode. On the standard category leaving
 * "free" needs an arrangement again, so the template's is proposed rather
 * than blanks.
 */
export const chargingPatch = (
    value: string,
    draft: CategoryConfigDraft,
    standard: StandardValues | undefined,
): Partial<CategoryConfigDraft> => {
    const proposal = standard?.template.payment;
    if (!proposal || value === 'free' || draft.paymentFlow !== '') return { charging: value };
    return {
        charging: value,
        paymentFlow: proposal.flow,
        paymentChannels: [...proposal.channels],
    };
};

/**
 * What differs between the two kinds of category in the ONE form (ADR-0026
 * §AG), in one place: the standard category offers no "inherits" — it IS the
 * standard — and saves a complete booking answer.
 */
export const formModeOf = (isStandard: boolean) => (isStandard
    ? {
        chargingOptions: ['free', 'mixed', 'chargeable'],
        directBookingOptions: ['true', 'false'],
        introKey: 'intro_standard',
        paymentFlowLabelKey: 'payment',
        paymentFlowHelpKey: 'payment_help' as string | undefined,
        toConfig: standardConfigFromDraft,
    }
    : {
        chargingOptions: [INHERIT, 'free', 'mixed', 'chargeable'],
        directBookingOptions: [INHERIT, 'true', 'false'],
        introKey: 'intro',
        paymentFlowLabelKey: 'payment_flow',
        paymentFlowHelpKey: undefined as string | undefined,
        toConfig: configFromDraft,
    });
